import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { secondAxios } from "../../../api/axios";
import useClientPlants from "../../../hooks/useClientPlants";
import {
  getProjectDocumentList,
  createDocumentList,
  ensureNumberedDocumentUid,
  documentAbbreviations,
} from "../../../pages/client/AllEquipments/CommonFeatures/DCSTab/Table/Dashboard/docListAPI";
import { getAllData } from "../../../pages/client/Ams/PV3/api";
import { getDrawings } from "../../../pages/client/AllEquipments/CommonFeatures/MCS3/BOMTab/DrawingRegisterTab/api";
import {
  PROJECT_DISCIPLINES,
  disciplineCode,
  projectTypeDiscipline,
  isProjectTypeIn,
  projectTypeStoredName,
  isEstimationProjectType,
} from "../../../constants/projectTypes";
import { PROJECT_SAVED_EVENT } from "./projectSavedEvent";

export { documentAbbreviations };

const SEARCH_DEBOUNCE_MS = 400;

export const wbsLabel = (wbs) => wbs?.name || wbs?.projectName || "Untitled WBS";

export const docLabel = (doc) =>
  doc?.documentNumber ||
  doc?.description ||
  doc?.documentName ||
  doc?.uid ||
  "Untitled document";

// The numbered "Doc No" identifier (e.g. "COR-001") - distinct from the
// longer composite DUID (documentNumber) - see DOCUMENT_REGISTER_GUIDE.md.
export const docNumberLabel = (doc) =>
  doc?.documentName || doc?.uid || doc?.docNumber || docLabel(doc);

export const matches = (label, query) => label.toLowerCase().includes(query);

export const isPvWbs = (wbs) => projectTypeDiscipline(wbs?.type) === "pv";

// Piping/Structural projects carry their own Estimation data directly on
// the project's revisions (each revision's `data.estimationTable`) - already
// embedded in the same /project/plant/:plantId/:clientName response
// loadWbsForPlant fetches, unlike PV which needs a separate /PV/all call.
export const isPipOrStrucWbs = (wbs) =>
  isProjectTypeIn(wbs?.type, ["pip", "struc"]);

// WBS under a plant are shown grouped by discipline rather than as one flat
// list, so a plant with dozens of them stays readable. The disciplines and
// their order come from PROJECT_DISCIPLINES so the tree can't drift from the
// type picker; anything with an unrecognised (or missing) `type` falls into
// "Other" instead of vanishing.
// Groups are labeled by the discipline's type code ("PVE", "PIE", …) rather
// than its full name, so the folder row stays narrow next to the WBS under it.
export const WBS_GROUPS = [
  ...PROJECT_DISCIPLINES.map((discipline) => ({
    ...discipline,
    label: disciplineCode(discipline.key),
  })),
  { key: "other", label: "Other" },
];

export const wbsGroupKey = (wbs) => projectTypeDiscipline(wbs?.type) || "other";

// Deliberately not prefixed "plant-"/"wbs-": handleNodeToggle keys the WBS
// and document fetches off those prefixes, and a group folder has nothing
// of its own to load.
export const wbsGroupNodeId = (plantId, groupKey) =>
  `grp-${plantId}-${groupKey}`;

// Discipline folders under "Projects" that list only Estimation-type WBS.
const ESTIMATION_ONLY_GROUPS = ["pv", "pip", "struc"];

/**
 * The folder a WBS sits in under "Projects". Pressure Vessel, Piping and
 * Structural list only Estimation WBS (PVE / PIE / STE); legacy "pv" / "pip" /
 * "struc" records count as "other" rather than sitting beside them as
 * look-alike duplicates.
 */
export const treeGroupKey = (wbs) => {
  const key = wbsGroupKey(wbs);
  if (
    ESTIMATION_ONLY_GROUPS.includes(key) &&
    !isEstimationProjectType(wbs?.type)
  ) {
    return "other";
  }
  return key;
};

/**
 * "other" WBS - legacy and unrecognised types ("pv", "pip", "struc", "ga", …)
 * the website no longer creates. The tree doesn't list them at all.
 */
export const isSoftwareWbs = (wbs) => treeGroupKey(wbs) === "other";

const parseMaybeJson = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    return JSON.parse(value) ?? {};
  } catch {
    return {};
  }
};

// One entry per Estimation revision. Those are not separate project revision
// records: the Structure/Piping modules open the project's latest record and
// keep every Estimation revision as a numeric key of its `estimationTable`
// (Rev 0, 1, 2 ... all saved into that one record - see savePipingData /
// saveStructuralData). So the keys are what gets listed, not the records.
export const extractProjectEstimationRevisions = (wbs) => {
  const records = (Array.isArray(wbs?.revisions) ? wbs.revisions : [])
    .map((rev) => parseMaybeJson(rev?.data))
    .filter((data) => data && !data.__structuralProjectRevisionDeleted);
  // Same record the modules load (the last one), so the tree lists exactly
  // the revisions the page's Rev. dropdown will offer.
  const latest = records[records.length - 1];
  const table = parseMaybeJson(latest?.estimationTable);

  return Object.keys(table)
    .filter((key) => key !== "" && !Number.isNaN(Number(key)))
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => ({ revision: key }));
};

// POs live on each MRQ (`mrq.pos`), not as a plant-level list, so flatten
// every MRQ's POs into the plant-level `pos` list the tree renders.
// Tolerates both `pos`/`bids` (documented shape, see MCS_API_BRIEF.md) and
// singular `po`/`bid` in case the payload uses those.
export const extractPlantPos = (mrqs) => {
  if (!Array.isArray(mrqs)) return [];
  return mrqs.flatMap((mrq) => {
    const list = Array.isArray(mrq?.pos)
      ? mrq.pos
      : Array.isArray(mrq?.POs)
        ? mrq.POs
        : Array.isArray(mrq?.po)
          ? mrq.po
          : [];
    return list;
  });
};

// One bid evaluation per MRQ that actually has vendor bids: a single MRQ can
// hold many vendor bids but that still counts as one evaluation. Rendered as
// the MRQ's own number + revision, keeping a back-reference to the source MRQ
// (and its bid count) for tooltips/navigation.
export const extractPlantBidEvaluations = (mrqs) => {
  if (!Array.isArray(mrqs)) return [];
  return mrqs
    .filter((mrq) => {
      const bids = Array.isArray(mrq?.bids)
        ? mrq.bids
        : Array.isArray(mrq?.bid)
          ? mrq.bid
          : Array.isArray(mrq?.Bids)
            ? mrq.Bids
            : null;
      return Array.isArray(bids) && bids.length > 0;
    })
    .map((mrq) => {
      const bids = Array.isArray(mrq?.bids)
        ? mrq.bids
        : Array.isArray(mrq?.bid)
          ? mrq.bid
          : mrq?.Bids;
      return {
        // One tree row per MRQ, so the MRQ id is the stable row id.
        id: mrq?.id,
        mrqId: mrq?.id,
        mrqNumber: mrq?.mrqNumber,
        rev: mrq?.rev,
        revision: mrq?.revision,
        mrqRevision: mrq?.mrqRevision,
        bidCount: Array.isArray(bids) ? bids.length : 0,
        // Kept for anything that needs the vendor rows later.
        bids,
        // Keep the source MRQ around without clobbering the flat fields above.
        mrq,
      };
    });
};

// Merge derived lists into an mcs-tree payload so `plantData.pos` /
// `plantData.bids` are always populated even when the backend only embeds
// them on each MRQ. Existing top-level entries win on id conflicts.
export const normalizeMcsTreeData = (raw) => {
  const data = raw && typeof raw === "object" ? raw : {};
  const mrqs = Array.isArray(data.mrqs) ? data.mrqs : [];

  const derivedPos = extractPlantPos(mrqs);
  const existingPos = Array.isArray(data.pos)
    ? data.pos
    : Array.isArray(data.posList)
      ? data.posList
      : [];
  const seenPoIds = new Set(existingPos.map((po) => po?.id).filter(Boolean));
  const mergedPos = [
    ...existingPos,
    ...derivedPos.filter((po) => !po?.id || !seenPoIds.has(po.id)),
  ];

  const derivedBids = extractPlantBidEvaluations(mrqs);
  const existingBids = Array.isArray(data.bids)
    ? data.bids
    : Array.isArray(data.bidEvaluations)
      ? data.bidEvaluations
      : [];
  const seenBidIds = new Set(existingBids.map((b) => b?.id).filter(Boolean));
  const mergedBids = [
    ...existingBids,
    ...derivedBids.filter((b) => !b?.id || !seenBidIds.has(b.id)),
  ];

  return { ...data, mrqs, pos: mergedPos, bids: mergedBids };
};

export const drawingLabel = (drawing) => {
  const base =
    drawing?.dwgNumber || drawing?.name || drawing?.title || "Untitled drawing";
  const details = [
    drawing?.revision != null && drawing?.revision !== ""
      ? `Rev ${drawing.revision}`
      : null,
    drawing?.sheetNumber ? `Sheet ${drawing.sheetNumber}` : null,
  ].filter(Boolean);
  return details.length > 0 ? `${base} (${details.join(", ")})` : base;
};

// PV projects store their saved revisions as an object keyed by revision
// number (see PVContext.jsx's savePVData/loadPvData). Each revision carries
// its own Input Data documentUID. The nested "estimation" record does NOT
// carry its own documentUID (verified against live data - savePVData only
// ever writes DataGridView2/7/8, bomData, and cost/hours totals in there) -
// so "has estimation" is judged by whether the BOM grid actually has rows,
// and shown labeled by revision, the same way Piping/Structural's Estimation
// entries are (they have no per-item document identifier either).
export const extractPvRevisionEntries = (allDataRes) => {
  const bucket = allDataRes?.all?.inputData;
  if (!bucket || typeof bucket !== "object") return [];

  return Object.entries(bucket)
    .filter(([key]) => !isNaN(Number(key)))
    .map(([revision, entry]) => {
      const estimation = entry?.estimation;
      const hasEstimationData =
        Array.isArray(estimation?.DataGridView8) &&
        estimation.DataGridView8.length > 0;

      return {
        revision,
        documentUID: entry?.documentUID || `Rev ${revision}`,
        estimationDocumentUID: hasEstimationData
          ? estimation?.documentUID || `Estimation — Rev ${revision}`
          : null,
      };
    })
    .sort((a, b) => Number(a.revision) - Number(b.revision));
};

/**
 * Data/state layer behind the Plant -> WBS -> Documents/Input Data/
 * Estimation/2D Drawings tree, shared by the global AppSidebar's
 * ProjectsTreeSection and the full-page EmsExplorer. Kept separate from
 * rendering so both consumers load and search the exact same way; each
 * renders it (and reacts to node clicks) differently.
 */
export default function useEmsProjectsTreeData() {
  const { plants, loading: plantsLoading, reloadPlants } = useClientPlants();
  const clientName = process.env.REACT_APP_CLIENT_NAME;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  // plantId -> "loading" | array of WBS
  const [wbsByPlant, setWbsByPlant] = useState({});
  // wbsId -> "loading" | array of documents
  const [docsByWbs, setDocsByWbs] = useState({});
  // wbsId -> "loading" | array of { revision, documentUID, estimationDocumentUID }
  const [pvInputsByWbs, setPvInputsByWbs] = useState({});
  // wbsId -> "loading" | array of drawing records (MCS3 Drawing Register)
  const [drawingsByWbs, setDrawingsByWbs] = useState({});
  // plantId -> "loading" | MCS tree data { mrqs, mrvs, srvs }
  const [mcsTreeByPlant, setMcsTreeByPlant] = useState({});
  const [expanded, setExpanded] = useState([]);

  const loadWbsForPlant = useCallback(
    (plantId, { force = false } = {}) => {
      setWbsByPlant((prev) => {
        if (prev[plantId] && !force) return prev;
        return { ...prev, [plantId]: "loading" };
      });
      secondAxios
        .get(`/project/plant/${plantId}/${clientName}`)
        .then((res) => {
          const items = Array.isArray(res.data) ? res.data : [];
          setWbsByPlant((prev) => ({ ...prev, [plantId]: items }));
        })
        .catch(() => {
          setWbsByPlant((prev) => ({ ...prev, [plantId]: [] }));
        });
    },
    [clientName],
  );

  const loadDocsForWbs = useCallback((wbsId, { force = false } = {}) => {
    setDocsByWbs((prev) => {
      if (prev[wbsId] && !force) return prev;
      return { ...prev, [wbsId]: "loading" };
    });
    getProjectDocumentList("all", wbsId)
      .then((docs) => {
        setDocsByWbs((prev) => ({
          ...prev,
          [wbsId]: Array.isArray(docs) ? docs : [],
        }));
      })
      .catch(() => {
        setDocsByWbs((prev) => ({ ...prev, [wbsId]: [] }));
      });
  }, []);

  // Creates a project (a plant - the tree's top level), then refreshes the
  // list so it appears straight away. Same endpoint and payload shape as the
  // Projects page's "Create New Project" dialog (Project/plant/index.jsx):
  // the name is "<client prefix>-<number>".
  const createPlant = useCallback(
    async ({ clientId, namePrefix, nameNumber, description, sharepointLink }) => {
      await secondAxios.post("/plant", {
        name: [namePrefix, nameNumber].filter(Boolean).join("-"),
        clientId: clientId || null,
        description: description?.trim() || null,
        sharepointLink: sharepointLink?.trim() || undefined,
      });
      reloadPlants();
    },
    [reloadPlants],
  );

  // Creates a new WBS/project under a plant (same endpoint as the
  // Project/plant page's SubProjectsCrudModal), then force-refreshes that
  // plant's WBS list so the tree reflects it immediately.
  const createWbs = useCallback(
    async (plantId, { projectName, type, link }) => {
      await secondAxios.post(`/project/create/${clientName}`, {
        projectName,
        clientName,
        link: link || "",
        // Saved under the full type name ("Estimation - Piping"), not the code.
        type: projectTypeStoredName(type),
        plantId,
      });
      loadWbsForPlant(plantId, { force: true });
    },
    [clientName, loadWbsForPlant],
  );

  // Creates a new Document Register row under a WBS, auto-numbering
  // its UID (e.g. COR-001) the same way the full Document Register editor
  // does (see DOCUMENT_REGISTER_GUIDE.md), then force-refreshes that WBS's
  // document list.
  const createDocument = useCallback(
    async (plant, wbs, { abbreviation, type, revision, description, numberOfSheets }) => {
      const existingDocs = Array.isArray(docsByWbs[wbs.id])
        ? docsByWbs[wbs.id]
        : [];
      const numbered = ensureNumberedDocumentUid(
        { documentName: abbreviation },
        existingDocs,
      );

      await createDocumentList(
        "all",
        wbs.id,
        {
          ...numbered,
          type,
          revision,
          description,
          numberOfSheets,
          projectNumber: plant?.name,
        },
        { wbsLabel: wbsLabel(wbs) },
      );
      loadDocsForWbs(wbs.id, { force: true });
    },
    [docsByWbs, loadDocsForWbs],
  );

  const loadPvInputsForWbs = useCallback((wbsId) => {
    setPvInputsByWbs((prev) => {
      if (prev[wbsId]) return prev;
      return { ...prev, [wbsId]: "loading" };
    });
    getAllData(wbsId)
      .then((res) => {
        setPvInputsByWbs((prev) => ({
          ...prev,
          [wbsId]: extractPvRevisionEntries(res),
        }));
      })
      .catch(() => {
        setPvInputsByWbs((prev) => ({ ...prev, [wbsId]: [] }));
      });
  }, []);

  const loadDrawingsForWbs = useCallback((wbsId) => {
    setDrawingsByWbs((prev) => {
      if (prev[wbsId]) return prev;
      return { ...prev, [wbsId]: "loading" };
    });
    getDrawings(wbsId)
      .then((drawings) => {
        setDrawingsByWbs((prev) => ({
          ...prev,
          [wbsId]: Array.isArray(drawings) ? drawings : [],
        }));
      })
      .catch(() => {
        setDrawingsByWbs((prev) => ({ ...prev, [wbsId]: [] }));
      });
  }, []);

  const loadMcsTreeForPlant = useCallback((plantId) => {
    setMcsTreeByPlant((prev) => {
      if (prev[plantId]) return prev;
      return { ...prev, [plantId]: "loading" };
    });
    secondAxios
      .get(`/plant/${plantId}/mcs-tree`)
      .then((res) => {
        // POs/Bids arrive embedded on each MRQ (`mrq.pos` / `mrq.bids`), so
        // flatten them here into plant-level `pos` / `bids` for the tree.
        setMcsTreeByPlant((prev) => ({
          ...prev,
          [plantId]: normalizeMcsTreeData(res.data),
        }));
      })
      .catch(() => {
        setMcsTreeByPlant((prev) => ({ ...prev, [plantId]: {} }));
      });
  }, []);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Structure/Piping keep their Estimation revisions inside the WBS list
  // response, which is cached per plant - so after a module saves, reload the
  // plant(s) holding that WBS, otherwise new revisions only show on a refresh.
  const wbsByPlantRef = useRef(wbsByPlant);
  wbsByPlantRef.current = wbsByPlant;
  useEffect(() => {
    const handleProjectSaved = (event) => {
      const projectId = String(event.detail?.projectId ?? "");
      if (!projectId) return;
      Object.entries(wbsByPlantRef.current).forEach(([plantId, list]) => {
        if (
          Array.isArray(list) &&
          list.some((wbs) => String(wbs?.id) === projectId)
        ) {
          loadWbsForPlant(plantId, { force: true });
        }
      });
    };
    window.addEventListener(PROJECT_SAVED_EVENT, handleProjectSaved);
    return () =>
      window.removeEventListener(PROJECT_SAVED_EVENT, handleProjectSaved);
  }, [loadWbsForPlant]);

  const query = debouncedSearch.toLowerCase();

  // Searching needs every WBS (and every WBS's documents) loaded to match
  // against — eager-load everything once a query is active.
  useEffect(() => {
    if (!query) return;
    plants.forEach((plant) => {
      if (!wbsByPlant[plant.id]) loadWbsForPlant(plant.id);
    });
  }, [query, plants, wbsByPlant, loadWbsForPlant]);

  useEffect(() => {
    if (!query) return;
    Object.values(wbsByPlant).forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach((wbs) => {
        if (!docsByWbs[wbs.id]) loadDocsForWbs(wbs.id);
      });
    });
  }, [query, wbsByPlant, docsByWbs, loadDocsForWbs]);

  const isSearchLoading =
    Boolean(query) &&
    (Object.values(wbsByPlant).some((v) => v === "loading") ||
      Object.values(docsByWbs).some((v) => v === "loading"));

  // Open just enough to put each match on screen, and no more: the plant and
  // the discipline folder above a matching WBS. Deliberately NOT expanded:
  // - a plant whose own name matched, since the match is the plant row itself
  //   and expanding it would unfurl every discipline and every WBS under it;
  // - the matching WBS node, whose contents nobody asked to see.
  // The tree is already filtered to matches, so anything worth opening is one
  // click away.
  //
  // What a WBS was left open from before the search is collapsed first, by the
  // effect below - otherwise a WBS the user had drilled into reappears among
  // the results with its documents, revisions and drawings unfurled, looking
  // like the search expanded it.
  const autoExpandIds = useMemo(() => {
    if (!query) return null;
    const ids = new Set();

    plants.forEach((plant) => {
      const wbsList = wbsByPlant[plant.id];
      if (!Array.isArray(wbsList)) return;

      wbsList.forEach((wbs) => {
        if (isSoftwareWbs(wbs)) return;
        const wbsMatches = matches(wbsLabel(wbs), query);
        const docs = docsByWbs[wbs.id];
        const hasDocMatch =
          Array.isArray(docs) &&
          docs.some((doc) => matches(docLabel(doc), query));

        if (wbsMatches || hasDocMatch) {
          ids.add(`plant-${plant.id}`);
          // The type folder sits between plant and WBS, so it has to open
          // too or the match stays hidden inside it.
          ids.add(wbsGroupNodeId(plant.id, treeGroupKey(wbs)));
        }
      });
    });

    return Array.from(ids);
  }, [query, plants, wbsByPlant, docsByWbs]);

  // Keyed off the query itself rather than autoExpandIds, which keeps changing
  // while a search's documents load - re-running this on that would fight the
  // user expanding a WBS to look inside a result.
  const lastQueryRef = useRef(query);
  useEffect(() => {
    if (query === lastQueryRef.current) return;
    lastQueryRef.current = query;
    if (!query) return;
    // Plants and discipline folders are what autoExpandIds is about to open
    // anyway; everything deeper belonged to the view the search replaced.
    setExpanded((prev) =>
      prev.filter((id) => id.startsWith("plant-") || id.startsWith("grp-")),
    );
  }, [query]);

  useEffect(() => {
    if (!autoExpandIds) return;
    setExpanded((prev) => Array.from(new Set([...prev, ...autoExpandIds])));
  }, [autoExpandIds]);

  const findWbsById = useCallback(
    (wbsId) => {
      for (const list of Object.values(wbsByPlant)) {
        if (!Array.isArray(list)) continue;
        const found = list.find((wbs) => wbs.id === wbsId);
        if (found) return found;
      }
      return null;
    },
    [wbsByPlant],
  );

  const handleNodeToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
    const newlyExpanded = nodeIds.filter((id) => !expanded.includes(id));
    newlyExpanded.forEach((id) => {
      if (id.startsWith("plant-")) {
        const plantId = id.slice("plant-".length);
        loadWbsForPlant(plantId);
        loadMcsTreeForPlant(plantId);
      } else if (id.startsWith("wbs-") && !id.includes("-cat-")) {
        // Category folder ids (Documents/Estimation/2D Drawings)
        // are built by appending "-cat-<name>" onto their parent wbs id, so
        // they still start with "wbs-" - excluded here, otherwise expanding
        // a category folder re-fires the doc/drawing fetches with a bogus
        // "id-cat-drawings" wbsId instead of the real one.
        const wbsId = id.slice("wbs-".length);
        loadDocsForWbs(wbsId);
        loadDrawingsForWbs(wbsId);
        if (isPvWbs(findWbsById(wbsId))) {
          loadPvInputsForWbs(wbsId);
        }
      }
    });
  };

  const isPlantVisible = (plant) => {
    if (!query) return true;
    if (matches(plant.name || "", query)) return true;
    const wbsList = wbsByPlant[plant.id];
    if (!Array.isArray(wbsList)) return false;
    return wbsList.some(
      (wbs) => !isSoftwareWbs(wbs) && isWbsVisible(plant, wbs),
    );
  };

  const isWbsVisible = (plant, wbs) => {
    if (!query) return true;
    if (matches(plant.name || "", query)) return true;
    if (matches(wbsLabel(wbs), query)) return true;
    const docs = docsByWbs[wbs.id];
    return (
      Array.isArray(docs) && docs.some((doc) => matches(docLabel(doc), query))
    );
  };

  const isDocVisible = (plant, wbs, doc) => {
    if (!query) return true;
    if (matches(plant.name || "", query)) return true;
    if (matches(wbsLabel(wbs), query)) return true;
    return matches(docLabel(doc), query);
  };

  return {
    plants,
    plantsLoading,
    search,
    handleSearchChange,
    isSearchLoading,
    query,
    wbsByPlant,
    docsByWbs,
    pvInputsByWbs,
    drawingsByWbs,
    mcsTreeByPlant,
    loadWbsForPlant,
    loadDocsForWbs,
    loadPvInputsForWbs,
    loadDrawingsForWbs,
    loadMcsTreeForPlant,
    createPlant,
    createWbs,
    createDocument,
    expanded,
    setExpanded,
    handleNodeToggle,
    isPlantVisible,
    isWbsVisible,
    isDocVisible,
  };
}
